import React, { useState, useEffect } from 'react';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import {
  FileUploader,
  FileUploaderContent,
  FileInput,
} from 'app/components/ui/file-uploader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import { Badge } from 'app/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'app/components/ui/collapsible';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from 'app/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/table';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Trash2,
  Package,
  DollarSign,
  Hash,
  Palette,
  Ruler,
  Weight,
  Image as ImageIcon,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useProductsSlice } from '../slice';
import { useMediaSlice } from 'app/pages/Media/slice';
import { useSearchParams } from 'react-router-dom';
import { ProductVariant, VariantFormData } from 'app/types/variant';
import { useFormContext } from 'react-hook-form';
import { mediaBaseUrl } from 'utils/api/endpoints';

// Dynamic validation schema for variant form
const createVariantSchema = (attributeTypes: any[]) => {
  const baseSchema = {
    variant_sku: z
      .string()
      .min(1, 'SKU is required')
      .max(100, 'SKU must be less than 100 characters'),
    variant_name: z
      .string()
      .min(1, 'Variant name is required')
      .max(255, 'Variant name must be less than 255 characters'),
    variant_price: z
      .string()
      .min(1, 'Price is required')
      .regex(/^\d+(\.\d{1,2})?$/, 'Invalid price format'),
    variant_stock_quantity: z
      .number()
      .min(0, 'Stock quantity must be 0 or greater'),
    variant_image_url: z.string().optional(),
    variant_image_file: z.any().optional(),
  };

  // Add dynamic attribute fields
  attributeTypes.forEach(attr => {
    baseSchema[attr.slug] = z.string().optional();
  });

  return z.object(baseSchema);
};

interface VariantFormProps {
  productId?: string | number;
  onVariantChange?: (variants: ProductVariant[]) => void;
  onPendingVariantsChange?: (variants: VariantFormData[]) => void;
  isNewProduct?: boolean;
}

export default function Variant({
  productId,
  onVariantChange,
  onPendingVariantsChange,
  isNewProduct = false,
}: VariantFormProps) {
  const { watch } = useFormContext();
  const variantMode = watch('variant_mode');

  const [searchParams] = useSearchParams();
  const productIdFromUrl = searchParams.get('id') || productId;
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingVariant, setEditingVariant] = useState<ProductVariant | null>(
    null,
  );
  const [originalFormData, setOriginalFormData] =
    useState<VariantFormData | null>(null);
  const [variants, setVariants] = useState<ProductVariant[]>([]);
  const [pendingVariants, setPendingVariants] = useState<VariantFormData[]>([]);
  const [attributeTypes, setAttributeTypes] = useState<any[]>([]);
  const [attributeValues, setAttributeValues] = useState<{
    [key: string]: any[];
  }>({});
  const [loadingAttributeValues, setLoadingAttributeValues] = useState<{
    [key: string]: boolean;
  }>({});
  const [selectedAttributes, setSelectedAttributes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedVariants, setExpandedVariants] = useState<{
    [key: number]: boolean;
  }>({});
  const [editingPendingVariant, setEditingPendingVariant] = useState<{
    [key: number]: VariantFormData;
  }>({});

  const [uploadedImages, setUploadedImages] = useState<{
    [key: string]: string;
  }>({});

  const {
    useGetProductVariantsQuery,
    useAddProductVariantMutation,
    useUpdateProductVariantMutation,
    useDeleteProductVariantMutation,
    useLazyGetProductsAttributeTypeQuery,
    useLazyGetSingleProductsAttributeTypeQuery,
  } = useProductsSlice();

  const { useUploadMediaMutation } = useMediaSlice();
  const [uploadMedia] = useUploadMediaMutation();

  const [
    getProductVariants,
    { data: variantsData, isLoading: isLoadingVariants },
  ] = useGetProductVariantsQuery();
  const [addVariant, { isLoading: isAddingVariant }] =
    useAddProductVariantMutation();
  const [updateVariant, { isLoading: isUpdatingVariant }] =
    useUpdateProductVariantMutation();
  const [deleteVariant, { isLoading: isDeletingVariant }] =
    useDeleteProductVariantMutation();
  const [getAttributeTypes, { data: attributeTypesData }] =
    useLazyGetProductsAttributeTypeQuery();
  const [getAttributeValues] = useLazyGetSingleProductsAttributeTypeQuery();

  // Create dynamic form based on available attributes
  const variantForm = useForm<VariantFormData>({
    resolver: zodResolver(createVariantSchema(attributeTypes)),
    defaultValues: {
      variant_sku: '',
      variant_name: '',
      variant_price: '',
      variant_stock_quantity: 0,
      variant_image_url: '',
      variant_image_file: [],
      ...attributeTypes.reduce((acc, attr) => {
        acc[attr.slug] = '';
        return acc;
      }, {}),
    },
  });

  // Load attribute types when component mounts
  useEffect(() => {
    getAttributeTypes({});
  }, [getAttributeTypes]);

  // Update attribute types when data is loaded
  useEffect(() => {
    if (attributeTypesData?.data) {
      setAttributeTypes(attributeTypesData.data);

      // Load attribute values for each type
      attributeTypesData.data.forEach(attr => {
        setLoadingAttributeValues(prev => ({ ...prev, [attr.id]: true }));
        loadAttributeValues(attr.id);
      });
    }
  }, [attributeTypesData]);

  // Load variants when component mounts
  useEffect(() => {
    if (productIdFromUrl) {
      console.log('Fetching variants for product ID:', productIdFromUrl);
      getProductVariants(productIdFromUrl);
    }
  }, [productIdFromUrl, getProductVariants]);

  // Update variants state when data is loaded
  useEffect(() => {
    if (variantsData?.data) {
      setVariants(variantsData.data);
      if (onVariantChange) {
        onVariantChange(variantsData.data);
      }
    }
  }, [variantsData, onVariantChange]);

  // Define createPendingVariants function before it's used in useEffect
  const createPendingVariants = async (newProductId: string | number) => {
    if (pendingVariants.length === 0) return;

    try {
      setLoading(true);

      for (const variantData of pendingVariants) {
        const payload = {
          product_id: parseInt(newProductId.toString()),
          ...variantData,
        };

        await addVariant(payload).unwrap();
      }

      toast.success(`${pendingVariants.length} variants created successfully`);
      setPendingVariants([]);

      // Refresh variants list
      if (productIdFromUrl) {
        getProductVariants(productIdFromUrl);
      }
    } catch (error) {
      console.error('Error creating pending variants:', error);
      toast.error('Failed to create some variants');
    } finally {
      setLoading(false);
    }
  };

  // Expose createPendingVariants function to parent component
  useEffect(() => {
    if (onPendingVariantsChange) {
      onPendingVariantsChange(pendingVariants);
    }
  }, [pendingVariants, onPendingVariantsChange]);

  // Expose createPendingVariants function via window for parent component access
  useEffect(() => {
    (window as any).createPendingVariants = createPendingVariants;
    return () => {
      delete (window as any).createPendingVariants;
    };
  }, [createPendingVariants]);

  // Don't render if not in variant mode
  if (variantMode !== 'variant') {
    return null;
  }

  // Get product type from URL or props
  const getProductType = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('product-type') || '1'; // Default to digital
  };

  // Get filtered attributes based on selection
  const getFilteredAttributes = () => {
    if (selectedAttributes.length === 0) {
      // If no attributes selected, show product-type relevant attributes
      const productType = getProductType();
      return attributeTypes.filter(attr => {
        if (
          attr.product_types &&
          attr.product_types.includes(parseInt(productType))
        ) {
          return true;
        }
        return false;
      });
    }

    // Show only selected attributes
    return attributeTypes.filter(attr =>
      selectedAttributes.includes(attr.id.toString()),
    );
  };

  // Load attribute values for a specific attribute type
  const loadAttributeValues = async (attributeTypeId: string) => {
    try {
      const response = await getAttributeValues(attributeTypeId).unwrap();
      if (response?.data) {
        setAttributeValues(prev => ({
          ...prev,
          [attributeTypeId]: response.data,
        }));
      }
    } catch (error) {
      console.error('Error loading attribute values:', error);
    } finally {
      setLoadingAttributeValues(prev => ({
        ...prev,
        [attributeTypeId]: false,
      }));
    }
  };

  // Handle add variant button click
  const handleAddVariantClick = async () => {
    console.log('Add variant button clicked');

    // Get current form data
    const currentData = variantForm.getValues();
    console.log('Current form data:', currentData);

    // Validate the form
    const isValid = await variantForm.trigger();
    if (!isValid) {
      console.log('Form validation failed');
      toast.error('Please fix the form errors before adding variant');
      return;
    }

    console.log('Form is valid, proceeding with add');

    if (isNewProduct || !productIdFromUrl) {
      // For new products, add to pending variants
      await handleAddPendingVariant(currentData);
    } else {
      // For existing products, create variant immediately
      await handleAddVariant(currentData);
    }
  };

  // Handle adding pending variant for new products
  const handleAddPendingVariant = async (data: VariantFormData) => {
    try {
      // Handle image upload if file is provided
      let imageFileName = data.variant_image_url || '';
      if (data.variant_image_file && data.variant_image_file.length > 0) {
        const uploadedFileName = await handleImageUpload(
          data.variant_image_file[0],
          'add',
        );
        if (uploadedFileName) {
          imageFileName = uploadedFileName;
        }
      }

      // Build variant payload with both text values and IDs for efficient filtering
      const pendingVariant: any = {
        variant_sku: data.variant_sku,
        variant_name: data.variant_name,
        variant_price: data.variant_price,
        variant_stock_quantity: parseInt(
          data.variant_stock_quantity.toString(),
        ),
        variant_image: imageFileName,
      };

      // Add attribute values (both text and IDs for backend filtering)
      attributeTypes.forEach(attr => {
        const value = data[attr.slug];
        if (value) {
          // Store as text value for display (e.g., "color": "Red")
          pendingVariant[attr.slug] = value;

          // Find and store the attribute value ID for efficient database filtering
          // This allows backend to use: WHERE color_id = 5 instead of WHERE color = 'Red'
          if (attributeValues[attr.id]) {
            const matchedValue = attributeValues[attr.id].find(
              (av: any) => av.name === value || av.slug === value,
            );
            if (matchedValue) {
              // Store ID for efficient filtering: "color_id": 5
              pendingVariant[`${attr.slug}_id`] = matchedValue.id;
            }
          }
        }
      });

      const updatedPendingVariants = [...pendingVariants, pendingVariant];
      setPendingVariants(updatedPendingVariants);

      // Notify parent component about pending variants
      if (onPendingVariantsChange) {
        onPendingVariantsChange(updatedPendingVariants);
      }

      toast.success('Variant added to pending list');
      resetVariantForm();
      setShowAddForm(false);
      setUploadedImages(prev => ({ ...prev, add: '' }));
    } catch (error) {
      console.error('Error adding pending variant:', error);
      toast.error('Failed to add variant');
    }
  };

  // Handle adding new variant
  const handleAddVariant = async (data: VariantFormData) => {
    try {
      setLoading(true);

      // Handle image upload if file is provided
      let imageFileName = data.variant_image_url || '';
      if (data.variant_image_file && data.variant_image_file.length > 0) {
        const uploadedFileName = await handleImageUpload(
          data.variant_image_file[0],
          'add',
        );
        if (uploadedFileName) {
          imageFileName = uploadedFileName;
        }
      }

      const payload = {
        product_id: parseInt(productIdFromUrl),
        ...data,
        variant_stock_quantity: parseInt(
          data.variant_stock_quantity.toString(),
        ),
        variant_image: imageFileName,
      };

      await addVariant(payload).unwrap();
      toast.success('Variant added successfully');
      resetVariantForm();
      setShowAddForm(false);

      // Refresh variants list
      getProductVariants(productIdFromUrl);
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to add variant');
    } finally {
      setLoading(false);
    }
  };

  // Check if form data has changed
  const hasFormDataChanged = (currentData: VariantFormData): boolean => {
    if (!originalFormData) return true;

    // Compare all relevant fields
    const fieldsToCompare = [
      'variant_sku',
      'variant_name',
      'variant_price',
      'variant_stock_quantity',
      'color',
      'size',
      'material',
      'weight',
      'dimensions',
      'variant_image_url',
    ];

    for (const field of fieldsToCompare) {
      if (currentData[field] !== originalFormData[field]) {
        return true;
      }
    }

    // Check if a new image file was uploaded
    if (
      currentData.variant_image_file &&
      currentData.variant_image_file.length > 0
    ) {
      return true;
    }

    return false;
  };

  // Handle updating variant
  const handleUpdateVariant = async (data: VariantFormData) => {
    console.log('handleUpdateVariant called with data:', data);
    console.log('editingVariant:', editingVariant);

    if (!editingVariant?.variant_id) {
      console.log('No editing variant ID, returning');
      return;
    }

    // Check if any changes were made
    if (!hasFormDataChanged(data)) {
      console.log('No changes detected, showing info toast');
      toast.info('No changes detected');
      setShowEditForm(false);
      setEditingVariant(null);
      setOriginalFormData(null);
      return;
    }

    console.log('Changes detected, proceeding with update');

    try {
      setLoading(true);

      // Handle image upload if file is provided
      let imageFileName = data.variant_image_url || '';
      if (data.variant_image_file && data.variant_image_file.length > 0) {
        const uploadedFileName = await handleImageUpload(
          data.variant_image_file[0],
          'edit',
        );
        if (uploadedFileName) {
          imageFileName = uploadedFileName;
        }
      }

      const payload = {
        variantId: editingVariant.variant_id,
        ...data,
        variant_stock_quantity: parseInt(
          data.variant_stock_quantity.toString(),
        ),
        variant_image: imageFileName,
      };

      await updateVariant(payload).unwrap();
      toast.success('Variant updated successfully');
      resetVariantForm();
      setShowEditForm(false);
      setEditingVariant(null);
      setOriginalFormData(null);

      // Refresh variants list
      getProductVariants(productIdFromUrl);
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to update variant');
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting variant
  const handleDeleteVariant = async (variantId: number) => {
    try {
      setLoading(true);
      await deleteVariant(variantId).unwrap();
      toast.success('Variant deleted successfully');

      // Refresh variants list
      getProductVariants(productIdFromUrl);
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to delete variant');
    } finally {
      setLoading(false);
    }
  };

  // Open edit dialog
  const openEditDialog = (variant: ProductVariant) => {
    console.log('openEditDialog called with variant:', variant);
    setEditingVariant(variant);
    const initialFormData = {
      variant_sku: variant.variant_sku,
      variant_name: variant.variant_name,
      variant_price: variant.variant_price,
      variant_stock_quantity: variant.variant_stock_quantity,
      variant_image_url: variant.variant_image_url || '',
      variant_image_file: [],
      // Include dynamic attributes from the variant
      ...attributeTypes.reduce((acc, attr) => {
        acc[attr.slug] = variant[attr.slug] || '';
        return acc;
      }, {}),
    };

    console.log('Setting original form data:', initialFormData);
    setOriginalFormData(initialFormData);
    variantForm.reset(initialFormData);
    setShowEditForm(true);
  };

  // Handle image upload
  const handleImageUpload = async (file: File, variantKey: string) => {
    try {
      const formData = new FormData();
      formData.append('products', file);

      const response = await uploadMedia(formData).unwrap();

      if (response?.response?.products?.[0]) {
        const fileName = response.response.products[0];
        // Store full URL for preview
        const imageUrl = `${mediaBaseUrl}/products/${fileName}`;
        setUploadedImages(prev => ({ ...prev, [variantKey]: imageUrl }));
        // Return just the filename for backend
        return fileName;
      } else {
        toast.error('Failed to upload image - invalid response');
        return null;
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error uploading image');
      return null;
    }
  };

  // Create all pending variants after product is created
  // Helper function to render dynamic attribute fields
  const renderAttributeFields = (attributes: any[]) => {
    return attributes.map((attr, index) => {
      const values = attributeValues[attr.id] || [];
      const isLoading = loadingAttributeValues[attr.id];
      const isEven = index % 2 === 0;

      return (
        <div
          key={attr.id}
          className={`space-y-2 ${isEven ? '' : 'md:col-start-2'}`}
        >
          <Label htmlFor={attr.slug}>{attr.name}</Label>
          <Select
            onValueChange={value => variantForm.setValue(attr.slug, value)}
            disabled={isLoading}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  isLoading
                    ? `Loading ${attr.name.toLowerCase()}...`
                    : `Select ${attr.name.toLowerCase()}`
                }
              />
            </SelectTrigger>
            <SelectContent>
              {values.map(value => (
                <SelectItem key={value.id} value={value.name}>
                  {value.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    });
  };

  // Helper function to render dynamic attribute fields for pending variants
  const renderAttributeFieldsForPending = (
    attributes: any[],
    variantIndex: number,
  ) => {
    return attributes.map((attr, index) => {
      const values = attributeValues[attr.id] || [];
      const isLoading = loadingAttributeValues[attr.id];
      const isEven = index % 2 === 0;

      // Get the current value from the variant (either edited or original)
      const currentVariant =
        editingPendingVariant[variantIndex] || pendingVariants[variantIndex];
      const currentValue = currentVariant?.[attr.slug] || '';

      return (
        <div
          key={attr.id}
          className={`space-y-2 ${isEven ? '' : 'md:col-start-2'}`}
        >
          <Label htmlFor={`${attr.slug}_${variantIndex}`}>{attr.name}</Label>
          <Select
            value={currentValue}
            onValueChange={value => {
              setEditingPendingVariant({
                ...editingPendingVariant,
                [variantIndex]: {
                  ...(pendingVariants[variantIndex] || {}),
                  ...(editingPendingVariant[variantIndex] || {}),
                  [attr.slug]: value,
                },
              });
            }}
            disabled={isLoading}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  isLoading
                    ? `Loading ${attr.name.toLowerCase()}...`
                    : `Select ${attr.name.toLowerCase()}`
                }
              />
            </SelectTrigger>
            <SelectContent>
              {values.map(value => (
                <SelectItem key={value.id} value={value.name}>
                  {value.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    });
  };

  // Helper function to reset form with dynamic attributes
  const resetVariantForm = () => {
    const defaultValues = {
      variant_sku: '',
      variant_name: '',
      variant_price: '',
      variant_stock_quantity: 0,
      variant_image_url: '',
      variant_image_file: [],
      ...attributeTypes.reduce((acc, attr) => {
        acc[attr.slug] = '';
        return acc;
      }, {}),
    };
    variantForm.reset(defaultValues);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center">
                <Package className="h-5 w-5 mr-2 text-blue-600" />
                Product Variants
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Manage different variations of this product (size, color, etc.)
              </p>
            </div>

            <div className="flex space-x-2">
              <Button
                size="sm"
                className="bg-primary hover:bg-primary/90"
                type="button"
                onClick={() => setShowAddForm(!showAddForm)}
              >
                <Plus className="h-4 w-4 mr-2" />
                {showAddForm ? 'Cancel' : 'Add Variant'}
              </Button>
            </div>
          </div>

          {/* Inline Add Variant Form */}
          {showAddForm && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-lg">Add New Variant</CardTitle>
              </CardHeader>
              <CardContent>
                <form
                  className="space-y-4"
                  onSubmit={e => {
                    e.preventDefault();
                    handleAddVariantClick();
                  }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="variant_sku">SKU *</Label>
                      <Input
                        id="variant_sku"
                        {...variantForm.register('variant_sku')}
                        placeholder="e.g., TSHIRT-ORG-S-BLK"
                      />
                      {variantForm.formState.errors.variant_sku && (
                        <p className="text-red-500 text-xs mt-1">
                          {variantForm.formState.errors.variant_sku.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="variant_name">Variant Name *</Label>
                      <Input
                        id="variant_name"
                        {...variantForm.register('variant_name')}
                        placeholder="e.g., Small Black"
                      />
                      {variantForm.formState.errors.variant_name && (
                        <p className="text-red-500 text-xs mt-1">
                          {variantForm.formState.errors.variant_name.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="variant_price">Price *</Label>
                      <Input
                        id="variant_price"
                        {...variantForm.register('variant_price')}
                        placeholder="29.99"
                        type="number"
                        step="0.01"
                      />
                      {variantForm.formState.errors.variant_price && (
                        <p className="text-red-500 text-xs mt-1">
                          {variantForm.formState.errors.variant_price.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="variant_stock_quantity">
                        Stock Quantity *
                      </Label>
                      <Input
                        id="variant_stock_quantity"
                        {...variantForm.register('variant_stock_quantity', {
                          valueAsNumber: true,
                        })}
                        placeholder="30"
                        type="number"
                        min="0"
                      />
                      {variantForm.formState.errors.variant_stock_quantity && (
                        <p className="text-red-500 text-xs mt-1">
                          {
                            variantForm.formState.errors.variant_stock_quantity
                              .message
                          }
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Attribute Selection */}
                  {attributeTypes.length > 0 && (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Select Attributes
                        </Label>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Choose which attributes to include for this variant
                        </p>
                        <div className="space-y-2">
                          <Select
                            onValueChange={value => {
                              if (
                                value &&
                                !selectedAttributes.includes(value)
                              ) {
                                setSelectedAttributes(prev => [...prev, value]);
                              }
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select an attribute to add" />
                            </SelectTrigger>
                            <SelectContent>
                              {attributeTypes
                                .filter(
                                  attr =>
                                    !selectedAttributes.includes(
                                      attr.id.toString(),
                                    ),
                                )
                                .map(attr => (
                                  <SelectItem
                                    key={attr.id}
                                    value={attr.id.toString()}
                                  >
                                    {attr.name}
                                  </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>

                          {/* Show selected attributes */}
                          {selectedAttributes.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {selectedAttributes.map(attrId => {
                                const attr = attributeTypes.find(
                                  a => a.id.toString() === attrId,
                                );
                                return attr ? (
                                  <div
                                    key={attrId}
                                    className="flex items-center space-x-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-md text-sm"
                                  >
                                    <span>{attr.name}</span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setSelectedAttributes(prev =>
                                          prev.filter(id => id !== attrId),
                                        )
                                      }
                                      className="ml-1 hover:text-blue-600 dark:hover:text-blue-300"
                                    >
                                      ×
                                    </button>
                                  </div>
                                ) : null;
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Selected Attributes */}
                  {getFilteredAttributes().length > 0 && (
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Selected Attributes
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {renderAttributeFields(getFilteredAttributes())}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 w-full">
                    <Label htmlFor="variant_image_file">Variant Image</Label>
                    <FileUploader
                      value={variantForm.watch('variant_image_file')}
                      onValueChange={async files => {
                        variantForm.setValue('variant_image_file', files);
                        if (files && files.length > 0) {
                          const file = files[0];
                          // Create preview URL for immediate feedback
                          const previewUrl = URL.createObjectURL(file);
                          setUploadedImages(prev => ({
                            ...prev,
                            add: previewUrl,
                          }));

                          // Upload to API
                          try {
                            const formData = new FormData();
                            formData.append('products', file);

                            const response = await uploadMedia(
                              formData,
                            ).unwrap();

                            if (response?.response?.products?.[0]) {
                              const fileName = response.response.products[0];
                              const imageUrl = `${mediaBaseUrl}/products/${fileName}`;

                              // Store filename in form field (for backend submission)
                              variantForm.setValue(
                                'variant_image_url',
                                fileName,
                              );

                              // Store full URL in uploadedImages for preview
                              setUploadedImages(prev => ({
                                ...prev,
                                add: imageUrl,
                              }));

                              toast.success('Image uploaded successfully');
                            } else {
                              toast.error(
                                'Failed to upload image - invalid response',
                              );
                            }
                          } catch (error) {
                            console.error('Image upload error:', error);
                            toast.error('Error uploading image');
                          }
                        } else {
                          // Clear image when removed
                          variantForm.setValue('variant_image_url', '');
                          const newUploadedImages = { ...uploadedImages };
                          delete newUploadedImages['add'];
                          setUploadedImages(newUploadedImages);
                        }
                      }}
                      dropzoneOptions={{
                        accept: {
                          'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
                        },
                        maxFiles: 1,
                        maxSize: 5 * 1024 * 1024, // 5MB
                        multiple: false,
                      }}
                    >
                      <FileUploaderContent>
                        <FileInput className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                          <div className="flex flex-col items-center space-y-2">
                            <div className="text-gray-500">
                              <svg
                                className="mx-auto h-12 w-12"
                                stroke="currentColor"
                                fill="none"
                                viewBox="0 0 48 48"
                              >
                                <path
                                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                  strokeWidth={2}
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <div className="text-sm text-gray-600">
                              <span className="font-medium">
                                Click to upload
                              </span>{' '}
                              or drag and drop
                            </div>
                            <p className="text-xs text-gray-500">
                              PNG, JPG, GIF up to 5MB
                            </p>
                          </div>
                        </FileInput>
                      </FileUploaderContent>
                    </FileUploader>
                    {uploadedImages['add'] && (
                      <div className="mt-2 relative inline-block">
                        <img
                          src={uploadedImages['add']}
                          alt="Preview"
                          className="w-20 h-20 object-cover rounded border"
                          onError={e => {
                            const target = e.target as HTMLImageElement;
                            if (target.parentElement) {
                              target.style.display = 'none';
                              const placeholder = document.createElement('div');
                              placeholder.className =
                                'w-20 h-20 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded border';
                              placeholder.innerHTML =
                                '<svg class="w-8 h-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>';
                              target.parentElement.appendChild(placeholder);
                            }
                          }}
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                          onClick={() => {
                            // Clear the image
                            variantForm.setValue('variant_image_file', []);
                            variantForm.setValue('variant_image_url', '');
                            // Remove from uploaded images
                            const newUploadedImages = { ...uploadedImages };
                            delete newUploadedImages['add'];
                            setUploadedImages(newUploadedImages);
                            toast.success('Image removed');
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                    {variantForm.formState.errors.variant_image_file && (
                      <p className="text-red-500 text-xs mt-1">
                        {
                          variantForm.formState.errors.variant_image_file
                            .message
                        }
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end space-x-2 py-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAddForm(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      disabled={loading || isAddingVariant}
                      onClick={handleAddVariantClick}
                    >
                      {isAddingVariant ? 'Adding...' : 'Add Variant'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Pending Variants for New Products */}
      {pendingVariants.length > 0 && (
        <div className="space-y-3 mb-6">
          {pendingVariants.map((variant, index) => (
            <Collapsible
              key={index}
              open={expandedVariants[index]}
              onOpenChange={open =>
                setExpandedVariants({
                  ...expandedVariants,
                  [index]: open,
                })
              }
            >
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    className="w-full h-auto p-0 hover:bg-transparent"
                  >
                    <div className="flex items-center justify-between p-4 cursor-pointer w-full">
                      <div className="flex items-center space-x-4 flex-1">
                        {/* Variant Image */}
                        <div className="w-16 h-16 flex-shrink-0 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 overflow-hidden">
                          {variant.variant_image_url ? (
                            <img
                              src={
                                variant.variant_image_url.startsWith('http')
                                  ? variant.variant_image_url
                                  : `${mediaBaseUrl}/products/${variant.variant_image_url}`
                              }
                              alt={variant.variant_name}
                              className="w-full h-full object-cover"
                              onError={e => {
                                const target = e.target as HTMLImageElement;
                                if (target.parentElement) {
                                  const placeholder =
                                    document.createElement('div');
                                  placeholder.className =
                                    'w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700';
                                  placeholder.innerHTML =
                                    '<svg class="w-6 h-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>';
                                  target.parentElement.replaceChild(
                                    placeholder,
                                    target,
                                  );
                                }
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-700">
                              <ImageIcon className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>

                        {/* Variant Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <p className="font-medium text-gray-900 dark:text-gray-100 truncate text-sm">
                              {variant.variant_name}
                            </p>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
                              {/* Attributes */}
                              {attributeTypes
                                .filter(attr => variant[attr.slug])
                                .map((attr, idx, array) => (
                                  <span key={attr.id}>
                                    <span className="font-medium">
                                      {attr.name}:
                                    </span>{' '}
                                    {variant[attr.slug]}
                                    {idx < array.length - 1 && (
                                      <span className="mx-2">|</span>
                                    )}
                                  </span>
                                ))}

                              {/* Separator between attributes and SKU/Stock */}
                              {attributeTypes.filter(attr => variant[attr.slug])
                                .length > 0 && <span className="mx-1">|</span>}

                              {/* SKU */}
                              <span>
                                <span className="font-medium">SKU:</span>{' '}
                                <span className="font-mono">
                                  {variant.variant_sku}
                                </span>
                              </span>

                              <span className="mx-1">|</span>

                              {/* Stock */}
                              <span>
                                <span className="font-medium">Stock:</span>{' '}
                                {variant.variant_stock_quantity}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center justify-center w-6 h-6 text-gray-400">
                          {expandedVariants[index] ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={e => {
                            e.stopPropagation();
                            const updatedPending = pendingVariants.filter(
                              (_, i) => i !== index,
                            );
                            setPendingVariants(updatedPending);
                            if (onPendingVariantsChange) {
                              onPendingVariantsChange(updatedPending);
                            }
                          }}
                          className="h-9 w-9 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </Button>
                </CollapsibleTrigger>

                {/* Collapsible Edit Form */}
                <CollapsibleContent>
                  <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <form
                      className="space-y-4"
                      onSubmit={e => {
                        e.preventDefault();
                        const editedVariant =
                          editingPendingVariant[index] || variant;
                        const updated = pendingVariants.map((v, i) =>
                          i === index ? editedVariant : v,
                        );
                        setPendingVariants(updated);
                        if (onPendingVariantsChange) {
                          onPendingVariantsChange(updated);
                        }
                        setExpandedVariants({
                          ...expandedVariants,
                          [index]: false,
                        });
                        // Clear editing state
                        const newEditingState = {
                          ...editingPendingVariant,
                        };
                        delete newEditingState[index];
                        setEditingPendingVariant(newEditingState);
                      }}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`variant_sku_${index}`}>SKU *</Label>
                          <Input
                            id={`variant_sku_${index}`}
                            defaultValue={variant.variant_sku}
                            onChange={e => {
                              setEditingPendingVariant({
                                ...editingPendingVariant,
                                [index]: {
                                  ...variant,
                                  ...(editingPendingVariant[index] || {}),
                                  variant_sku: e.target.value,
                                },
                              });
                            }}
                            placeholder="e.g., TSHIRT-ORG-S-BLK"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor={`variant_name_${index}`}>
                            Variant Name *
                          </Label>
                          <Input
                            id={`variant_name_${index}`}
                            defaultValue={variant.variant_name}
                            onChange={e => {
                              setEditingPendingVariant({
                                ...editingPendingVariant,
                                [index]: {
                                  ...variant,
                                  ...(editingPendingVariant[index] || {}),
                                  variant_name: e.target.value,
                                },
                              });
                            }}
                            placeholder="e.g., Small Black"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`variant_price_${index}`}>
                            Price *
                          </Label>
                          <Input
                            id={`variant_price_${index}`}
                            defaultValue={variant.variant_price}
                            onChange={e => {
                              setEditingPendingVariant({
                                ...editingPendingVariant,
                                [index]: {
                                  ...variant,
                                  ...(editingPendingVariant[index] || {}),
                                  variant_price: e.target.value,
                                },
                              });
                            }}
                            placeholder="29.99"
                            type="number"
                            step="0.01"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor={`variant_stock_${index}`}>
                            Stock Quantity *
                          </Label>
                          <Input
                            id={`variant_stock_${index}`}
                            defaultValue={variant.variant_stock_quantity}
                            onChange={e => {
                              setEditingPendingVariant({
                                ...editingPendingVariant,
                                [index]: {
                                  ...variant,
                                  ...(editingPendingVariant[index] || {}),
                                  variant_stock_quantity:
                                    parseInt(e.target.value) || 0,
                                },
                              });
                            }}
                            placeholder="30"
                            type="number"
                            min="0"
                          />
                        </div>
                      </div>

                      {/* Attribute Selection */}
                      {attributeTypes.length > 0 && (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              Select Attributes
                            </Label>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              Choose which attributes to include for this
                              variant
                            </p>
                            <div className="space-y-2">
                              <Select
                                onValueChange={value => {
                                  if (value && !selectedAttributes[value]) {
                                    setSelectedAttributes(prev => {
                                      if (prev.includes(value)) {
                                        return prev;
                                      }
                                      return [...prev, value];
                                    });
                                  }
                                }}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select an attribute to add" />
                                </SelectTrigger>
                                <SelectContent>
                                  {attributeTypes
                                    .filter(
                                      attr =>
                                        !selectedAttributes.includes(
                                          attr.id.toString(),
                                        ),
                                    )
                                    .map(attr => (
                                      <SelectItem
                                        key={attr.id}
                                        value={attr.id.toString()}
                                      >
                                        {attr.name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>

                              {/* Show selected attributes */}
                              {selectedAttributes.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {selectedAttributes.map(attrId => {
                                    const attr = attributeTypes.find(
                                      a => a.id.toString() === attrId,
                                    );
                                    return attr ? (
                                      <div
                                        key={attrId}
                                        className="flex items-center space-x-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-md text-sm"
                                      >
                                        <span>{attr.name}</span>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setSelectedAttributes(prev =>
                                              prev.filter(id => id !== attrId),
                                            )
                                          }
                                          className="ml-1 hover:text-blue-600 dark:hover:text-blue-300"
                                        >
                                          ×
                                        </button>
                                      </div>
                                    ) : null;
                                  })}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Selected Attributes - Auto-detect from variant */}
                          {(() => {
                            const currentVariant =
                              editingPendingVariant[index] ||
                              pendingVariants[index];
                            const variantAttributes = attributeTypes.filter(
                              attr => attr.slug && currentVariant?.[attr.slug],
                            );

                            return variantAttributes.length > 0 ? (
                              <div className="space-y-4">
                                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  Selected Attributes
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {renderAttributeFieldsForPending(
                                    variantAttributes,
                                    index,
                                  )}
                                </div>
                              </div>
                            ) : selectedAttributes.length > 0 ? (
                              <div className="space-y-4">
                                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  Selected Attributes
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {renderAttributeFieldsForPending(
                                    attributeTypes.filter(attr =>
                                      selectedAttributes.includes(
                                        attr.id.toString(),
                                      ),
                                    ),
                                    index,
                                  )}
                                </div>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      )}

                      {/* Variant Image */}
                      <div className="space-y-2">
                        <Label htmlFor={`variant_image_${index}`}>
                          Variant Image
                        </Label>
                        <Input
                          type="file"
                          accept="image/*"
                          className="cursor-pointer"
                          onChange={async e => {
                            const files = e.target.files;
                            if (!files || files.length === 0) return;

                            const file = files[0];

                            // Create preview URL for immediate feedback
                            const previewUrl = URL.createObjectURL(file);
                            setUploadedImages(prev => ({
                              ...prev,
                              [`pending_${index}`]: previewUrl,
                            }));

                            // Update state
                            setEditingPendingVariant({
                              ...editingPendingVariant,
                              [index]: {
                                ...variant,
                                ...(editingPendingVariant[index] || {}),
                                variant_image_file: file,
                              },
                            });

                            // Upload to API
                            try {
                              const formData = new FormData();
                              // Use 'products' as the FormData key to match API
                              formData.append('products', file);

                              const response = await uploadMedia(
                                formData,
                              ).unwrap();

                              if (response?.response?.products?.[0]) {
                                const fileName = response.response.products[0];
                                // Prepend the base URL to construct the full image URL
                                const imageUrl = `${mediaBaseUrl}/products/${fileName}`;

                                // Update with the uploaded image URL
                                setEditingPendingVariant({
                                  ...editingPendingVariant,
                                  [index]: {
                                    ...variant,
                                    ...(editingPendingVariant[index] || {}),
                                    variant_image_url: imageUrl,
                                  },
                                });

                                // Update the preview with actual image URL
                                setUploadedImages(prev => ({
                                  ...prev,
                                  [`pending_${index}`]: imageUrl,
                                }));

                                toast.success('Image uploaded successfully');
                              } else {
                                toast.error(
                                  'Failed to upload image - invalid response',
                                );
                              }
                            } catch (error) {
                              console.error('Image upload error:', error);
                              toast.error('Error uploading image');
                            }
                          }}
                        />
                        {uploadedImages[`pending_${index}`] ||
                          editingPendingVariant[index]?.variant_image_url ||
                          variant.variant_image_url ? (
                          <div className="mt-2 relative inline-block">
                            <img
                              src={(() => {
                                const previewUrl =
                                  uploadedImages[`pending_${index}`];
                                const editingUrl =
                                  editingPendingVariant[index]
                                    ?.variant_image_url;
                                const originalUrl = variant.variant_image_url;

                                // uploadedImages contains full URLs
                                if (previewUrl) return previewUrl;

                                // Check if URLs are filenames (no http/https) - prepend base URL
                                const urlToUse = editingUrl || originalUrl;
                                if (urlToUse && !urlToUse.startsWith('http')) {
                                  return `${mediaBaseUrl}/products/${urlToUse}`;
                                }
                                return urlToUse || '';
                              })()}
                              alt="Preview"
                              className="w-20 h-20 object-cover rounded border"
                              onError={e => {
                                const target = e.target as HTMLImageElement;
                                if (target.parentElement) {
                                  target.style.display = 'none';
                                  const placeholder =
                                    document.createElement('div');
                                  placeholder.className =
                                    'w-20 h-20 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded border';
                                  placeholder.innerHTML =
                                    '<svg class="w-8 h-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>';
                                  target.parentElement.appendChild(placeholder);
                                }
                              }}
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="destructive"
                              className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                              onClick={() => {
                                // Clear the image
                                setEditingPendingVariant({
                                  ...editingPendingVariant,
                                  [index]: {
                                    ...variant,
                                    ...(editingPendingVariant[index] || {}),
                                    variant_image_url: '',
                                    variant_image_file: [],
                                  },
                                });
                                // Remove from uploaded images
                                const newUploadedImages = {
                                  ...uploadedImages,
                                };
                                delete newUploadedImages[`pending_${index}`];
                                setUploadedImages(newUploadedImages);
                                toast.success('Image removed');
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="mt-2 w-20 h-20 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded border">
                            <ImageIcon className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                      </div>

                      <div className="flex justify-end space-x-2 pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setExpandedVariants({
                              ...expandedVariants,
                              [index]: false,
                            });
                            // Clear editing state on cancel
                            const newEditingState = {
                              ...editingPendingVariant,
                            };
                            delete newEditingState[index];
                            setEditingPendingVariant(newEditingState);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button type="submit">Save Changes</Button>
                      </div>
                    </form>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
        </div>
      )}

      {/* Variants List */}
      {isLoadingVariants ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : variants.length === 0 ? (
        <Card className="p-8 text-center">
          <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-2">
            No Variants Yet
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
            Add variants to offer different options for this product.
          </p>
          <Button onClick={() => setShowAddForm(true)} type="button">
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Variant
          </Button>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Variants ({variants.length})</span>
              <Badge variant="secondary">
                {variants.reduce((sum, v) => sum + v.variant_stock_quantity, 0)}{' '}
                Total Stock
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>SKU</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Stock</TableHead>
                    <TableHead>Attributes</TableHead>
                    <TableHead>Image</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {variants.map(variant => (
                    <TableRow key={variant.variant_id}>
                      <TableCell className="font-medium">
                        <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-sm">
                          {variant.variant_sku}
                        </code>
                      </TableCell>
                      <TableCell>{variant.variant_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <DollarSign className="h-4 w-4 mr-1 text-green-600" />
                          {variant.variant_price}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center">
                          <Hash className="h-4 w-4 mr-1 text-blue-600" />
                          {variant.variant_stock_quantity}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 space-x-1">
                          {variant.color && (
                            <Badge variant="outline" className="text-xs">
                              <Palette className="h-3 w-3 mr-1" />
                              {variant.color}
                            </Badge>
                          )}
                          {variant.size && (
                            <Badge variant="outline" className="text-xs">
                              <Ruler className="h-3 w-3 mr-1" />
                              {variant.size}
                            </Badge>
                          )}
                          {variant.material && (
                            <Badge variant="outline" className="text-xs">
                              <Weight className="h-3 w-3 mr-1" />
                              {variant.material}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {variant.variant_image_url ? (
                          <img
                            src={
                              variant.variant_image_url.startsWith('http')
                                ? variant.variant_image_url
                                : `${mediaBaseUrl}/products/${variant.variant_image_url}`
                            }
                            alt={variant.variant_name}
                            className="w-10 h-10 object-cover rounded"
                            onError={e => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center">
                            <ImageIcon className="h-4 w-4 text-gray-400" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditDialog(variant)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                                disabled={loading || isDeletingVariant}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Delete Variant
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this variant?
                                  This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() =>
                                    handleDeleteVariant(variant.variant_id!)
                                  }
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inline Edit Form */}
      {showEditForm && editingVariant && (
        <Card className="mt-4">
          <CardHeader>
            <CardTitle className="text-lg">Edit Variant</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-4"
              onSubmit={e => {
                e.preventDefault();
                handleUpdateVariant();
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_variant_sku">SKU *</Label>
                  <Input
                    id="edit_variant_sku"
                    {...variantForm.register('variant_sku')}
                    placeholder="e.g., TSHIRT-ORG-S-BLK"
                  />
                  {variantForm.formState.errors.variant_sku && (
                    <p className="text-red-500 text-xs mt-1">
                      {variantForm.formState.errors.variant_sku.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="edit_variant_name">Variant Name *</Label>
                  <Input
                    id="edit_variant_name"
                    {...variantForm.register('variant_name')}
                    placeholder="e.g., Small Black"
                  />
                  {variantForm.formState.errors.variant_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {variantForm.formState.errors.variant_name.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit_variant_price">Price *</Label>
                  <Input
                    id="edit_variant_price"
                    {...variantForm.register('variant_price')}
                    placeholder="29.99"
                    type="number"
                    step="0.01"
                  />
                  {variantForm.formState.errors.variant_price && (
                    <p className="text-red-500 text-xs mt-1">
                      {variantForm.formState.errors.variant_price.message}
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="edit_variant_stock_quantity">
                    Stock Quantity *
                  </Label>
                  <Input
                    id="edit_variant_stock_quantity"
                    {...variantForm.register('variant_stock_quantity', {
                      valueAsNumber: true,
                    })}
                    placeholder="30"
                    type="number"
                    min="0"
                  />
                  {variantForm.formState.errors.variant_stock_quantity && (
                    <p className="text-red-500 text-xs mt-1">
                      {
                        variantForm.formState.errors.variant_stock_quantity
                          .message
                      }
                    </p>
                  )}
                </div>
              </div>

              {/* Selected Attributes */}
              {getFilteredAttributes().length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Selected Attributes
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {getFilteredAttributes().map((attr, index) => {
                      const values = attributeValues[attr.id] || [];
                      const isLoading = loadingAttributeValues[attr.id];
                      const isEven = index % 2 === 0;

                      return (
                        <div
                          key={attr.id}
                          className={`space-y-2 ${isEven ? '' : 'md:col-start-2'
                            }`}
                        >
                          <Label htmlFor={`edit_${attr.slug}`}>
                            {attr.name}
                          </Label>
                          <Select
                            value={variantForm.watch(attr.slug)}
                            onValueChange={value =>
                              variantForm.setValue(attr.slug, value)
                            }
                            disabled={isLoading}
                          >
                            <SelectTrigger>
                              <SelectValue
                                placeholder={
                                  isLoading
                                    ? `Loading ${attr.name.toLowerCase()}...`
                                    : `Select ${attr.name.toLowerCase()}`
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {values.map(value => (
                                <SelectItem key={value.id} value={value.name}>
                                  {value.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="edit_variant_image_file">Variant Image</Label>
                <FileUploader
                  value={variantForm.watch('variant_image_file')}
                  onValueChange={files => {
                    variantForm.setValue('variant_image_file', files);
                    if (files && files.length > 0) {
                      // Create preview URL
                      const previewUrl = URL.createObjectURL(files[0]);
                      setUploadedImages(prev => ({
                        ...prev,
                        edit: previewUrl,
                      }));
                    }
                  }}
                  dropzoneOptions={{
                    accept: {
                      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
                    },
                    maxFiles: 1,
                    maxSize: 5 * 1024 * 1024, // 5MB
                    multiple: false,
                  }}
                >
                  <FileUploaderContent>
                    <FileInput className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                      <div className="flex flex-col items-center space-y-2">
                        <div className="text-gray-500">
                          <svg
                            className="mx-auto h-12 w-12"
                            stroke="currentColor"
                            fill="none"
                            viewBox="0 0 48 48"
                          >
                            <path
                              d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">Click to upload</span>{' '}
                          or drag and drop
                        </div>
                        <p className="text-xs text-gray-500">
                          PNG, JPG, GIF up to 5MB
                        </p>
                      </div>
                    </FileInput>
                  </FileUploaderContent>
                </FileUploader>
                {uploadedImages['edit'] && (
                  <div className="mt-2">
                    <img
                      src={uploadedImages['edit']}
                      alt="Preview"
                      className="w-20 h-20 object-cover rounded border"
                    />
                  </div>
                )}
                {editingVariant?.variant_image_url && !uploadedImages['edit'] && (
                  <div className="mt-2">
                    <img
                      src={editingVariant.variant_image_url}
                      alt="Current"
                      className="w-20 h-20 object-cover rounded border"
                    />
                    <p className="text-xs text-gray-500 mt-1">Current image</p>
                  </div>
                )}
                {variantForm.formState.errors.variant_image_file && (
                  <p className="text-red-500 text-xs mt-1">
                    {variantForm.formState.errors.variant_image_file.message}
                  </p>
                )}
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowEditForm(false);
                    setEditingVariant(null);
                    setOriginalFormData(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={loading || isUpdatingVariant}>
                  {isUpdatingVariant ? 'Updating...' : 'Update Variant'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
